<template>
  <div class="media-detail-container">
    <b-modal
      ref="mediaDetailModal"
      v-model="visible"
      modal-class="media-preview-modal"
      centered
      :hide-header="true"
      :hide-footer="true"
      :disable-close="isMobile"
      ignore-enforce-focus-selector=".modal-aula"
      @shown="setMediaLimit"
      @hidden="closeMediaDetail"
      @closeClicked="closeMediaDetail()"
    >
      <div class="no-print">
        <mobile-topbar
          v-if="isMobile"
          :header-text-from-textkey="false"
          :text="activeAlbum && activeAlbum.title ? activeAlbum.title : ''"
          @leftIconClicked="
            $refs.mediaDetailModal.hide();
            closeMediaDetail();
          "
        />
        <b-row class="m-0">
          <b-col :lg="fullscreenMode ? '12' : '8'" md="12" sm="12" xl="9" class="p-0">
            <BButton
              v-if="isMobile && !showMediaDetails"
              variant="light"
              class="open-media-detail text-uppercase"
              @click="showMediaDetails = true"
            >
              {{ $getText('GALLERY_SHOW_INFO') }}
            </BButton>
            <template v-if="slide !== -1">
              <b-carousel
                :value="slide"
                class="media-carousel media-detail"
                :class="{ fromSearch: activeMedia.fromSearch }"
                :controls="!isMobile"
                :interval="0"
                @sliding-start="updateSlide"
              >
                <b-carousel-slide v-for="media in mediaList" :key="media.id">
                  <AulaImg
                    v-if="media.mediaType === mediaTypes.IMAGE"
                    slot="img"
                    class="img"
                    :img-url="media.file.url"
                  />
                  <video
                    v-if="media.mediaType === mediaTypes.VIDEO"
                    :id="`video${media.id}`"
                    slot="img"
                    class="video"
                    controls
                  />
                  <audio
                    v-if="media.mediaType == mediaTypes.SOUND"
                    slot="img"
                    class="audio"
                    :src="media.file.url"
                    controls
                  />
                  <template v-for="(tag, j) in filteredTags">
                    <Avatar
                      v-if="children && children.find(child => child.id == tag.id) != null"
                      :key="j"
                      :profile="tag"
                    />
                  </template>
                </b-carousel-slide>
              </b-carousel>
            </template>
            <div v-if="!fullscreenMode && !activeMedia.fromSearch" class="thumbnails-container">
              <div
                v-if="!isMobile"
                class="media-thumbnails-prev"
                :aria-label="$getText('ARIA_LABEL_PREVIOUS_MEDIA')"
                tabindex="0"
                role="button"
                @click="showPrevThumbnails"
                @keydown.enter="showPrevThumbnails"
              >
                <i class="icon-Aula_back-arrow" />
              </div>
              <div ref="mediaThumbnails" class="media-thumbnails">
                <div
                  v-for="(media, i) in albumMedia"
                  v-show="i >= thumbnailStartIndex && i <= thumbnailEndIndex"
                  :key="media.id"
                  v-button
                  class="small-thumbnail"
                  tabindex="0"
                  :aria-selected="media.id === activeMedia.id"
                  @click="slide = i"
                  @keydown.enter="slide = i"
                >
                  <VideoThumbnail v-if="media.mediaType === mediaTypes.VIDEO" :media="media" />
                  <SoundThumbnail v-if="media.mediaType === mediaTypes.SOUND" />
                  <AulaImg
                    v-if="media.mediaType === mediaTypes.IMAGE"
                    :img-url="media.extraSmallThumbnailUrl"
                    :alt="media.title"
                  />
                </div>
              </div>
              <div
                v-if="!isMobile"
                class="media-thumbnails-next"
                :aria-label="$getText('ARIA_LABEL_NEXT_MEDIA')"
                tabindex="0"
                role="button"
                @click="showNextThumbnails"
                @keydown.enter="showNextThumbnails"
              >
                <i class="icon-Aula_forward-arrow" />
              </div>
            </div>
          </b-col>
          <b-col
            v-if="
              activeMedia != null &&
              activeMedia.id != null &&
              (!isMobile || (isMobile && showMediaDetails)) &&
              !fullscreenMode
            "
            lg="4"
            xl="3"
            sm="12"
            md="12"
            class="p-0 media-information"
            :class="{ 'information-overlay': isMobile }"
          >
            <div class="media-details">
              <div class="media-actions">
                <b-dropdown class="dropdown-select foldermenu" :right="isMobile ? true : false">
                  <template slot="button-content">
                    <i class="icon-Aula_elipses" :aria-label="$getText('ARIA_LABEL_GALLERY_MEDIA_OPTIONS')" />
                  </template>
                  <b-dropdown-item v-if="canDeleteMedia" @click="$refs.deleteMediaModal.show()">
                    {{ $getText('GALLERY_DELETE_MEDIA') }}
                  </b-dropdown-item>
                  <b-dropdown-item @click="downloadMedia">
                    {{ $getText('GALLERY_DOWNLOAD_MEDIA') }}
                  </b-dropdown-item>
                  <b-dropdown-item v-if="activeMedia.currentUserCanReport" @click="openReportMediaModal()">
                    {{ $getText('REPORTED_CONTENT_REPORT') }}
                  </b-dropdown-item>
                </b-dropdown>
              </div>
              <b-button variant="link" class="close-button" @click="$refs.mediaDetailModal.hide()">
                <Icon :name="iconClassEnum.CLOSE" />
              </b-button>
              <div class="media-creator">
                <ContactAvatar :profile="activeMedia.creator" />
                <div class="media-creator-infor">
                  <div class="media-creator-name">
                    {{ $getProfileDisplayName(activeMedia.creator) }}
                  </div>
                  <div class="media-date">{{ mediaCreatedLongDate }}</div>
                </div>
                <b-btn
                  v-if="isMobile"
                  tabindex="0"
                  class="ml-auto p-1"
                  variant="link"
                  :aria-label="$getText('ARIA_LABEL_CLOSE')"
                  @click="showMediaDetails = false"
                  @keydown.enter="showMediaDetails = false"
                >
                  <i class="icon icon-Aula_close" />
                  <span class="sr-only">{{ $getText('ARIA_LABEL_DELETE') }}</span>
                </b-btn>
              </div>
              <div class="media-info mt-2">
                <div
                  v-if="
                    activeMedia.conversionStatus === conversionStatus.FAILED &&
                    activeMedia.mediaType === mediaTypes.VIDEO
                  "
                  class="failed-conversion-container"
                >
                  <AulaInfoTrigger class="ml-2" area="VIDEO_CONVERSION_FAILED" />
                  {{ $getText('GALLERY_CONVERSION_FAILED_TITLE') }}
                </div>
                <h2>{{ activeMedia.title }}</h2>
                <div class="media-description">
                  {{ activeMedia.description }}
                </div>
                <template v-if="shouldShowTags">
                  <div class="media-tags" :class="!isMobile ? 'mt-4' : ''">
                    <div v-if="filteredTags.length > 0" class="media-tag-recipients pl-2">
                      {{ $getText('GALLERY_TAGS') }}:
                      <template>
                        {{ $getProfileDisplayName(filteredTags[0]) }}
                      </template>
                    </div>
                    <b-dropdown
                      v-if="!isMobile && showTagsDropdown()"
                      ref="mediaTagHandling"
                      class="dropdown-select foldermenu tag-panel"
                      right
                      :popper-opts="{ positionFixed: true }"
                    >
                      <template slot="button-content"> {{ tagsDetail }} <i class="icon-Aula_down-arrow" /> </template>
                      <MediaTagHandling
                        :is-mobile="isMobile"
                        :existing-tags="filteredTags"
                        :can-edit-tags="activeMedia.currentUserCanEditTags"
                        :disable-search="disableTagSearch"
                        :institution-code="albumInstitutionCode"
                        @cancelTagging="cancelTagging"
                        @addTagsToMedia="addTagsToMedia"
                        @removeTagFromMedia="removeTagFromMedia"
                      />
                    </b-dropdown>
                    <BButton
                      v-if="isMobile && !isReadOnlyTagsParent"
                      variant="link"
                      @click="showTagDetailsMobile = true"
                      >{{ tagsDetail }}
                      <i class="icon-Aula_edit_pen" :aria-label="$getText('ARIA_LABEL_EDIT')" /></BButton
                    ><br />
                    <b-btn
                      v-if="isMobile && activeMedia.currentUserCanEditTags && !isReadOnlyTagsParent"
                      variant="link"
                      @click="showTagDetailsMobile = true"
                    >
                      <span class="button-text">{{ $getText('GALLERY_EDIT_TAGS') }}</span>
                      <i
                        class="icon-Aula_forward-arrow ml-2"
                        style="font-size: 10px"
                        :aria-label="$getText('ARIA_LABEL_EDIT')"
                      /> </b-btn
                    ><br />
                    <b-btn
                      v-if="
                        isMobile &&
                        (parent == parentTypes.GALLERY ||
                          parent == parentTypes.GROUP_GALLERY ||
                          parent == parentTypes.SEARCH)
                      "
                      variant="link"
                      @click="showCommentDetailsMobile = true"
                    >
                      {{ activeMedia.commentCount }}
                      {{ $getText('COMMENTS_COMMENTS') }}
                      <i class="icon-Aula_forward-arrow ml-2" style="font-size: 10px" />
                    </b-btn>
                  </div>

                  <div
                    v-if="
                      activeAlbum != null &&
                      activeAlbum.sharedWithGroups != null &&
                      activeAlbum.sharedWithGroups.length > 0 &&
                      (parent == parentTypes.GALLERY || parent == parentTypes.GROUP || activeMedia.fromSearch)
                    "
                    class="media-shared-with d-flex align-items-center"
                    :class="!isMobile ? 'mt-4' : ''"
                  >
                    {{ $getText('GALLERY_SHARED_WITH') }}
                    <template v-for="(group, i) in activeAlbum.sharedWithGroups">
                      <template v-if="i < 1">
                        {{ group.name }}
                        <template v-if="group.portalRoles != null">
                          (<template v-for="(role, i) in group.portalRoles">
                            {{ $getText('GROUP_LABEL_TYPE_' + role.toUpperCase()) }}
                            <template v-if="i < group.portalRoles.length - 1"> - </template> </template
                          >)
                        </template>
                      </template>
                    </template>

                    <b-dropdown
                      v-if="activeAlbum.sharedWithGroups.length > 1"
                      ref="sharedWithDropdown"
                      class="dropdown-select foldermenu ml-1"
                      right
                      variant="link"
                    >
                      <template slot="button-content">
                        {{ '+ ' + (activeAlbum.sharedWithGroups.length - 1) + ' andre' }}
                        <i class="icon-Aula_down-arrow pl-2" />
                      </template>
                      <template v-for="(group, i) in activeAlbum.sharedWithGroups">
                        <div v-if="i > 0" :key="i">
                          {{ group.name }}
                          <template v-if="group.portalRoles != null">
                            (<template v-for="(role, i) in group.portalRoles">
                              {{ $getText('GROUP_LABEL_TYPE_' + role.toUpperCase()) }}
                              <template v-if="i < group.portalRoles.length - 1"> - </template> </template
                            >)
                          </template>
                        </div>
                      </template>
                    </b-dropdown>
                  </div>
                  <comments
                    v-if="
                      activeMedia.canViewComments &&
                      !isMobile &&
                      (parent == parentTypes.GALLERY ||
                        parent == parentTypes.GROUP_GALLERY ||
                        parent == parentTypes.SEARCH)
                    "
                    :can-comment="activeMedia.allowsComments"
                    :parent-id="activeMedia.id"
                    :parent-type="commentTypes.MEDIA"
                    :comment-count="activeMedia.commentCount"
                    :clear-all="clearAll"
                    @commentAdded="updateMediaComment"
                  />
                </template>
              </div>
            </div>
          </b-col>
        </b-row>
        <transition v-if="isMobile" name="slide-only">
          <div v-if="showTagDetailsMobile" class="media-tag-details-mobile">
            <MediaTagHandling
              :is-mobile="isMobile"
              :existing-tags="filteredTags"
              :can-edit-tags="activeMedia.currentUserCanEditTags"
              :disable-search="disableTagSearch"
              :institution-code="albumInstitutionCode"
              @cancelTagging="hideTagDetailsMobile"
              @addTagsToMedia="addTagsToMedia"
              @removeTagFromMedia="removeTagFromMedia"
            />
          </div>
        </transition>
        <transition v-if="isMobile" name="slide-only">
          <div v-if="showCommentDetailsMobile" class="media-tag-details-mobile">
            <mobile-topbar text-key="COMMENTS_COMMENTS" @leftIconClicked="showCommentDetailsMobile = false" />
            <div class="p-2">
              <comments
                v-if="activeMedia.canViewComments"
                :can-comment="activeMedia.allowsComments"
                :parent-id="activeMedia.id"
                :parent-type="commentTypes.MEDIA"
                :comment-count="activeMedia.commentCount"
                :clear-all="clearAll"
                @commentAdded="updateMediaComment"
              />
            </div>
          </div>
        </transition>
      </div>
    </b-modal>
    <BModal
      ref="deleteMediaModal"
      cancel-variant="link"
      :title="$t('label.warning')"
      :ok-title="$t('button.yes')"
      :cancel-title="$t('button.cancel')"
      @cancel="$refs.deleteMediaModal.hide(), $refs.mediaDetailModal.show()"
      @ok="deleteActiveMedia"
    >
      {{ $getText('GALLERY_DELETE_MEDIA_CONFIRM') }}
      <p v-if="activeMedia.fromSearch">
        {{ $getText('GALLERY_DELETE_MEDIA_FROM_SEARCH') }}
      </p>
    </BModal>
    <aula-modal
      ref="cancelCommentWhenUpdatingSlideModal"
      ok-text="YES"
      @okClicked="onConfirmCancelCommentWhenUpdatingSlide"
      @cancelClicked="goBackToComment()"
    >
      {{ $getText('COMMENTS_CONFIRM_CANCEL_COMMENT') }}<br />
      {{ $getText('COMMENTS_CONFIRM_CANCEL_COMMENT_2') }}
    </aula-modal>
    <aula-modal
      ref="cancelCommentWhenCloseModal"
      ok-text="YES"
      @okClicked="
        updateWritingComment(false);
        nextRoute();
      "
      @cancelClicked="$refs.cancelCommentWhenCloseModal.hide()"
    >
      {{ $getText('COMMENTS_CONFIRM_CANCEL_COMMENT') }}<br />
      {{ $getText('COMMENTS_CONFIRM_CANCEL_COMMENT_2') }}
    </aula-modal>
    <BModal
      ref="reportContentModal"
      modal-class="report-content-modal"
      :title="$getText('REPORTED_CONTENT_HEADER')"
      :ok-title="$getText('REPORTED_CONTENT_SEND')"
      :cancel-title="$getText('CANCEL')"
      cancel-variant="link"
      body-class="bg-grey-light"
      footer-class="bg-grey-light"
      :ok-disabled="isSubmittingReport"
      :busy="isSubmittingReport"
      @ok="saveReportMedia"
      @cancel="$refs.cancelReportModal.show()"
    >
      {{ $getText('REPORTED_CONTENT_WRITE_A_REASON') }}
      <b-form-textarea
        v-model="reportedContentReason"
        :placeholder="$getText('REPORTED_CONTENT_FIELDS_REASON')"
        @input="showWarningReasonLength = false"
      />
      <b-alert variant="danger" :show="showWarningReasonLength">
        {{ $getText(warningReasonLength) }}
      </b-alert>
      {{ $getText('REPORTED_CONTENT_NOTE') }}
    </BModal>
    <BModal
      ref="cancelReportModal"
      modal-class="cancel-report-modal"
      :title="$t('label.warning')"
      :ok-title="$getText('OK')"
      :cancel-title="$getText('CANCEL')"
      cancel-variant="link"
      body-class="bg-grey-light"
      footer-class="bg-grey-light"
      @ok="$refs.cancelReportModal.hide()"
      @cancel="$refs.reportContentModal.show()"
    >
      {{ $getText('REPORTED_CONTENT_CANCEL_REPORT_1') }}<br />
      {{ $getText('REPORTED_CONTENT_CANCEL_REPORT_2') }}
    </BModal>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { types } from '../../store/types/types';
import $ from 'jquery';
import uniqBy from 'lodash/uniqBy';
import uniq from 'lodash/uniq';
import MobileTopbar from '../../../shared/components/MobileTopbar';
import { docTypes } from '../../../shared/enums/docTypes';
import { portalRoles } from '../../../shared/enums/portalRoles';
import { mediaTypes } from '../../../shared/enums/mediaTypes';
import { parentTypes } from '../../../shared/enums/parentTypes.ts';
import { moduleTypes } from '../../../shared/enums/moduleTypes';
import { commentTypes } from '../../../shared/enums/commentTypes';
import Comments from '../../../shared/components/Comments';
import AulaImg from '../../../shared/components/AulaImg';
import { providerKeyTypes } from '../../../shared/enums/providerKeyTypes';
import { conversionStatusTypes } from '../../../shared/enums/conversionStatusTypes';
import MediaTagHandling from '../../../shared/PageFragments/Media/MediaTagHandling';
import VideoThumbnail from '../../../shared/PageFragments/Media/VideoThumbnail.vue';
import SoundThumbnail from '../../../shared/PageFragments/Media/SoundThumbnail.vue';
import Button from '../../../shared/directives/button';
import { mediaFilterType } from '../../../shared/enums/mediaFilterType';
import Avatar from '../../../shared/components/Avatar.vue';
import ContactAvatar from '../contacts/ContactAvatar.vue';
import { DateTimeUtil } from '../../../shared/utils/dateTimeUtil';
import { useUserProfileStore } from '../../../shared/stores';
import { ScanningStatusEnum } from '../../../shared/enums/scanningStatus.enum';
import Icon from '../../../shared/components/Icon.vue';
import { iconClassEnum } from '../../../shared/enums/iconClassEnum';
import AulaInfoTrigger from '../../../shared/components/AulaInfoTrigger.vue';
import isEmpty from 'lodash/isEmpty';

const MEDIA_PRINT_WINDOW_STYLES = `
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html, body {
    background-color: white;
    font-family: Arial, sans-serif;
  }

  .content {
    padding: 2px;
    text-align: center;
  }

  img {
    display: block;
    margin: 0 auto;
    max-width: 100%;
    height: auto;
  }

  h2 {
    margin: 10px 0;
  }

  p {
    margin: 5px 0;
  }

  @media print {
    html, body {
      background-color: white !important;
    }

    .content {
      width: 100vw;
      height: 100vh;
      padding: 2mm;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
    }

    img {
      max-width: 100%;
      max-height: 90vh;
      width: auto;
      height: auto;
      object-fit: contain;
      page-break-inside: avoid;
    }
  }
`;

const DEFAULT_MEDIA_LIMIT = 9;

export default {
  components: {
    Icon,
    AulaInfoTrigger,
    ContactAvatar,
    Avatar,
    SoundThumbnail,
    VideoThumbnail,
    MediaTagHandling,
    AulaImg,
    Comments,
    MobileTopbar,
  },
  directives: {
    Button: Button,
  },
  inject: {
    onRemoveMediaFromAlbum: {
      from: providerKeyTypes.onRemoveMediaFromAlbum,
      default: () => () => null,
    },
  },
  provide() {
    return {
      [providerKeyTypes.onClickPrint]: () => this.handlePrint(),
    };
  },
  props: {
    mediaId: { type: Number, default: null },
    album: { type: Array, default: () => [] },
    parent: { type: String, default: parentTypes.GALLERY },
    sortOn: { type: String },
    orderDirection: { type: String },
    filterBy: { type: String, default: mediaFilterType.ALL },
    filterInstProfileIds: { type: Array, default: () => [] },
  },
  data: function () {
    return {
      slide: -1,
      visible: true,
      thumbnailStartIndex: 0,
      thumbnailEndIndex: DEFAULT_MEDIA_LIMIT - 1,
      docTypes: docTypes,
      portalRoles: portalRoles,
      mediaTypes: mediaTypes,
      parentTypes: parentTypes,
      moduleTypes: moduleTypes,
      commentTypes: commentTypes,
      showMediaDetails: false,
      showSharedWithDropdown: false,
      showTagDetailsMobile: false,
      showCommentDetailsMobile: false,
      clearAll: false,
      fullscreenMode: false,
      profileSelected: false,
      childSelected: [],
      albumMedia: [],
      reportedContentReason: '',
      isSubmittingReport: false,
      nextRoute: '',
      prevSlide: '',
      showWarningReasonLength: false,
      warningReasonLength: null,
      mediaLimit: null,
      excludeTagIdDictionary: {},
      conversionStatus: conversionStatusTypes,
    };
  },
  computed: {
    iconClassEnum() {
      return iconClassEnum;
    },
    ...mapGetters({
      activeGroup: types.GET_ACTIVE_GROUP,
      children: types.GET_CHILDREN,
      isMobile: types.GET_IS_MOBILE,
      activeAlbum: types.GET_ACTIVE_ALBUM,
      institutions: types.GET_INSTITUTIONS,
      hasPermission: types.HAS_PERMISSION,
      hasPermissionGroup: types.HAS_PERMISSION_ON_GROUP,
      hasPermissionOnInstitution: types.HAS_PERMISSION_ON_INSTITUTION,
      writingComment: types.GET_WRITING_COMMENT,
      userGroups: types.GET_USER_GROUPS,
    }),
    userProfileStore() {
      return useUserProfileStore();
    },
    profile() {
      return this.userProfileStore.institutionProfile;
    },
    mediaList() {
      if (this.albumMedia.length === 0) {
        return [this.activeMedia];
      }
      return this.albumMedia;
    },
    filteredTags() {
      return this.activeMedia.tags?.filter(tag => this.excludeTagIdDictionary[tag.id] !== true) || [];
    },
    albumInstitutionCode() {
      if (this.activeAlbum && this.activeAlbum.creator) {
        return this.activeAlbum.creator.institutionCode;
      }
      return null;
    },
    tagsDetail() {
      if (this.otherTaggedUsersCount > 0) {
        return '+ ' + this.otherTaggedUsersCount + ' ' + this.$getText('OTHERS');
      }

      return this.$getText('TAG_MORE');
    },
    disableTagSearch() {
      return this.activeMedia.currentUserCanEditTags != null && !this.activeMedia.currentUserCanEditTags;
    },
    // Surfaces that show media as a preview (Secure Documents, Messages) treat
    // tags as read-only display. Existing tags still appear in
    // `media-tag-recipients`, but no add/edit affordance is exposed.
    isReadOnlyTagsParent() {
      return this.parent === parentTypes.DOCUMENTS || this.parent === parentTypes.MESSAGES;
    },
    activeMedia() {
      if (this.albumMedia[this.slide] != null) {
        return this.albumMedia[this.slide];
      } else {
        return {};
      }
    },
    mediaCreatedLongDate() {
      return DateTimeUtil.longDate(this.activeMedia?.file?.created, true);
    },
    otherTaggedUsersCount() {
      return this.activeMedia.tags.length - 1;
    },
    canDeleteMedia() {
      return this.activeMedia.currentUserCanDelete;
    },
    shouldShowTags() {
      return this.parent !== parentTypes.NOTICE_BOARDS;
    },
  },
  watch: {
    activeMedia() {
      if (this.activeMedia.mediaType === mediaTypes.VIDEO) {
        this.applyVideo(this.activeMedia);
      }
    },
    mediaLimit: {
      async handler(newVal, oldVal) {
        if (newVal === oldVal) {
          return;
        }
        if (newVal > oldVal && this.albumMedia.length < newVal) {
          const roomForMoreMedia = newVal - this.albumMedia.length;

          if (roomForMoreMedia > 0) {
            await this.getMoreMedia(this.albumMedia.length, roomForMoreMedia);
          }
        }
      },
    },
  },
  beforeDestroy() {
    this.albumMedia = null;
  },
  mounted() {
    this.initMedia();
    window.addEventListener('resize', this.setMediaLimit);
    window.addEventListener('keydown', this.onKeyDown);
  },
  destroyed() {
    window.removeEventListener('resize', this.setMediaLimit);
    window.removeEventListener('keydown', this.onKeyDown);
  },
  methods: {
    ...mapActions({
      deleteMedia: types.DELETE_MEDIA,
      getAlbumMedia: types.ACTION_GET_ALBUM_DETAILS,
      updateMedia: types.UPDATE_ATTACHMENTS,
      reportMedia: types.REPORT_MEDIA,
      loadVideoFromSource: types.VC_LOAD_VIDEO_FROM_SOURCE,
    }),
    ...mapMutations({
      updateWritingComment: types.MUTATE_WRITING_COMMENT,
      removeMediaInStore: types.MUTATE_DELETE_MEDIA,
      setHasMediaChanged: types.MUTATE_HAS_MEDIA_CHANGED,
    }),
    showTagsDropdown() {
      if (this.isReadOnlyTagsParent) {
        return false;
      }
      if (!this.activeMedia.currentUserCanEditTags) {
        return this.activeMedia.tags.length > 1;
      }
      return true;
    },
    updateMediaComment() {
      this.activeMedia.commentCount = this.activeMedia.commentCount == null ? 1 : this.activeMedia.commentCount + 1;
    },
    deleteActiveMedia() {
      this.deleteMedia({ mediaIds: [this.activeMedia.id] });
      this.$refs.deleteMediaModal.hide();
      this.visible = true;
      this.$refs.mediaDetailModal.show();
      this.removeMediaFromAlbumAndGoToNextSlide();
    },
    openReportMediaModal() {
      this.reportedContentReason = '';
      this.isSubmittingReport = false;
      this.resetCommentForm();
      this.$refs.reportContentModal.show();
    },
    resetCommentForm() {
      this.showWarningReasonLength = false;
      this.warningReasonLength = null;
    },
    saveReportMedia(bvModalEvent) {
      if (bvModalEvent) {
        bvModalEvent.preventDefault();
      }
      if (this.isSubmittingReport) {
        return;
      }
      if (this.reportedContentReason.length < 10 || this.reportedContentReason.length > 255) {
        this.showWarningReasonLength = true;
        this.warningReasonLength =
          this.reportedContentReason.length < 10
            ? 'REPORTED_CONTENT_REASON_LENGTH_WARNING_TOO_SHORT'
            : 'REPORTED_CONTENT_REASON_LENGTH_WARNING_TOO_LONG';
      } else {
        this.resetCommentForm();
        this.isSubmittingReport = true;
        this.reportMedia({
          id: this.activeMedia.id,
          reportReason: this.reportedContentReason,
        })
          .then(() => {
            this.$refs.reportContentModal.hide();
            this.reportedContentReason = '';
            this.removeMediaInStore({ mediaIds: [this.activeMedia.id] });
            this.removeMediaFromAlbumAndGoToNextSlide();
            this.setHasMediaChanged(true);

            if (this.albumMedia.length === 0) {
              this.closeMediaDetail();
            }
          })
          .finally(() => {
            this.isSubmittingReport = false;
          });
      }
    },
    async showNextThumbnails() {
      if (this.thumbnailEndIndex + this.mediaLimit > this.albumMedia.length) {
        await this.getMoreMedia(this.albumMedia.length, this.mediaLimit - (this.albumMedia.length % this.mediaLimit));
        this.setThumbnailIndexes(this.albumMedia.length - this.mediaLimit);
      } else {
        if (this.thumbnailStartIndex + this.mediaLimit < this.albumMedia.length) {
          this.setThumbnailIndexes(this.thumbnailEndIndex);
        }
      }
    },
    showPrevThumbnails() {
      if (this.thumbnailStartIndex - this.mediaLimit <= 0) {
        this.setThumbnailIndexes(0);
      } else {
        this.setThumbnailIndexes(this.thumbnailStartIndex - this.mediaLimit);
      }
    },
    onConfirmCancelCommentWhenUpdatingSlide() {
      this.updateWritingComment(false);
      this.$refs.cancelCommentWhenUpdatingSlideModal.hide();
      this.clearAll = !this.clearAll;
      if (typeof this.nextRoute === 'function') {
        this.nextRoute();
      }
    },
    goBackToComment() {
      this.$refs.cancelCommentWhenUpdatingSlideModal.hide();
      this.updateWritingComment(false);
      this.confirmUpdateSlide(this.prevSlide);
      setTimeout(() => {
        this.updateWritingComment(true);
      }, 50);
    },
    updateSlide(slide) {
      if (this.writingComment) {
        if (this.slide !== slide) {
          this.prevSlide = this.slide;
        }
        this.nextRoute = () => this.confirmUpdateSlide(slide);
        this.$refs.cancelCommentWhenUpdatingSlideModal.show();
      } else {
        this.confirmUpdateSlide(slide);
      }
    },
    async confirmUpdateSlide(slide) {
      if (this.parent !== parentTypes.USERDATA) {
        $('.video,.audio').each(function () {
          this.pause();
        });
        if (this.thumbnailEndIndex + this.mediaLimit > this.albumMedia.length) {
          await this.getMoreMedia(this.albumMedia.length, this.mediaLimit - (this.albumMedia.length % this.mediaLimit));
        }
        const maxStartIndex = Math.max(0, this.albumMedia.length - this.mediaLimit);
        if (slide > this.thumbnailEndIndex) {
          if (slide >= maxStartIndex) {
            this.setThumbnailIndexes(maxStartIndex);
          } else {
            this.setThumbnailIndexes(slide);
          }
        } else if (slide < this.thumbnailStartIndex) {
          const newStart = Math.max(0, slide - ((this.mediaLimit || DEFAULT_MEDIA_LIMIT) - 1));
          this.setThumbnailIndexes(newStart);
        }
        this.prevSlide = this.slide;
        this.slide = slide;
        if (this.mediaId != null) {
          return;
        }
        const mediaId = this.albumMedia[slide]?.id;
        const params = this.$route.params;
        if (!isEmpty(params) && mediaId != null) {
          params.mediaId = mediaId;
          this.$router.push({ name: this.$route.name, params: params });
        }
      }
    },
    applyVideo(media) {
      this.loadVideoFromSource(media.file).then(objectUrl => {
        if (objectUrl == null) {
          return;
        }
        const videoElement = document.getElementById('video' + media.id);
        if (videoElement.src !== objectUrl) {
          videoElement.src = objectUrl;
        }
      });
    },
    downloadMedia() {
      window.location = this.activeMedia.file.url;
    },
    removeMediaFromAlbumAndGoToNextSlide() {
      this.$emit('mediaDelete', this.activeMedia.id);
      if (this.albumMedia.length === 1) {
        this.closeMediaDetail();
        return;
      }

      const mediaIndex = this.slide;
      const activeMedia = this.albumMedia[mediaIndex];
      this.albumMedia = this.albumMedia.filter(media => media.id != this.activeMedia.id);
      let nextMedia;
      this.onRemoveMediaFromAlbum(activeMedia);

      if (this.albumMedia[this.slide]) {
        nextMedia = this.albumMedia[this.slide];
      } else {
        nextMedia = this.albumMedia[0];
        this.slide = 0;
      }

      if (this.$route.name === 'myMediaDetails') {
        this.$router.push({
          name: 'myMediaDetails',
          params: { mediaId: nextMedia.id },
        });
      } else if (this.$route.name === 'group-media-details') {
        this.$router.push({
          name: 'group-album-details',
          params: {
            groupId: this.$route.params.groupId,
            albumId: this.activeAlbum.id,
          },
        });
      }
    },
    cancelTagging() {
      this.$refs.mediaTagHandling.hide();
    },
    hideTagDetailsMobile() {
      this.showTagDetailsMobile = false;
    },
    addTagsToMedia(tags) {
      let tagIds = this.activeMedia.tags.map(tag => tag.id);
      if (tags.length > 0) {
        const newUserIds = tags.map(tag => tag.id);
        tagIds = uniq(tagIds.concat(newUserIds));
        const media = {
          media: [
            {
              id: this.activeMedia.id,
              tags: tagIds,
              description: this.activeMedia.description,
              title: this.activeMedia.title,
            },
          ],
        };
        if (this.activeMedia.tags.filter(tag => tag.id == tags.id).length == 0) {
          this.updateMedia(media).then(() => {
            this.activeMedia.tags = uniqBy(this.activeMedia.tags.concat(tags), tag => tag.id);
            this.$emit('tagsUpdated', this.activeMedia.tags);
          });
        }
      }
    },
    removeTagFromMedia(mediaTag) {
      this.excludeTagIdDictionary = {
        ...this.excludeTagIdDictionary,
        [mediaTag.id]: true,
      };
      const media = {
        media: [
          {
            id: this.activeMedia.id,
            tags: this.filteredTags.map(tag => tag.id),
            description: this.activeMedia.description,
            title: this.activeMedia.title,
          },
        ],
      };
      this.updateMedia(media)
        .then(() => {
          const tagIndex = this.activeMedia.tags.findIndex(tag => tag.id === mediaTag.id);
          this.activeMedia.tags.splice(tagIndex, 1);
          this.$emit('tagsUpdated', this.activeMedia.tags);
          this.hasUserPermissionToViewMediaAfterRemovingTag(mediaTag);
        })
        .finally(() => {
          delete this.excludeTagIdDictionary[mediaTag.id];
        });
    },
    hasUserPermissionToViewMediaAfterRemovingTag(mediaTag) {
      if (
        !this.activeAlbum &&
        this.$route.name === 'myMediaDetails' &&
        this.institutions.find(i => i.institutionProfileId === mediaTag.id) != null &&
        this.userGroups.filter(g => this.activeMedia.sharedGroupIds.includes(g.id)).length === 0
      ) {
        this.removeMediaInStore({ mediaIds: [this.activeMedia.id] });
        this.removeMediaFromAlbumAndGoToNextSlide();
      }
    },
    closeMediaDetail() {
      if (this.writingComment) {
        this.$refs.mediaDetailModal.show();
        this.nextRoute = this.confirmCloseMediaDetails;
        this.$refs.cancelCommentWhenCloseModal.show();
      } else {
        this.confirmCloseMediaDetails();
      }
    },
    closeMediaDetailInGallery() {
      if (this.activeAlbum != null) {
        if (this.albumMedia.length != this.album.length) {
          this.getMoreMedia(0, 11);
        }
        this.$router.push('/galleri/' + this.activeAlbum.id);
      } else {
        this.$router.push({ name: 'myMedias' });
      }
    },
    closeMediaDetailInCalendar() {
      switch (this.$route.name) {
        case 'viewMediaEvent':
          this.$router.push({
            name: 'viewEvent',
            params: { eventId: this.$route.params.eventId },
          });
          break;
        case 'viewMediaMeetingEvent':
          this.$router.push({
            name: 'viewMeetingEvent',
            params: { eventId: this.$route.params.eventId },
          });
          break;
        case 'group-calendar-view-media-event':
          this.$router.push({
            name: 'group-calendar-view-event',
            params: {
              groupId: this.$route.params.groupId,
              eventId: this.$route.params.eventId,
            },
          });
          break;
        case 'group-calendar-view-media-meeting-event':
          this.$router.push({
            name: 'group-calendar-view-meeting-event',
            params: {
              groupId: this.$route.params.groupId,
              eventId: this.$route.params.eventId,
            },
          });
          break;
        default:
          this.$router.push({ name: 'calendar' });
      }
    },
    confirmCloseMediaDetails() {
      this.$emit('onClosed');
      if (this.mediaId) {
        return;
      }

      switch (this.parent) {
        case parentTypes.GROUP_GALLERY:
          this.$router.push({
            name: 'group-album-details',
            params: {
              groupId: this.activeGroup.id,
              albumId: this.activeAlbum.id,
            },
          });
          break;
        case parentTypes.GROUP_CALENDAR:
          this.$router.push({
            name: 'group-calendar',
            params: { groupId: this.activeGroup.id },
          });
          break;
        case parentTypes.NOTICE_BOARDS:
          this.$router.push({ name: 'notice-board' });
          break;
        case parentTypes.GALLERY:
          this.closeMediaDetailInGallery();
          break;
        case parentTypes.CALENDAR:
          this.closeMediaDetailInCalendar();
          break;
        case parentTypes.MESSAGES:
          this.$router.push({
            name: 'uniqueThread',
            params: { id: this.$route.params.id },
          });
          break;
        default:
          return;
      }
    },
    setMediaLimit() {
      const thumbnailWidth = this.$refs.mediaThumbnails?.clientWidth;
      if (!thumbnailWidth) {
        return;
      }
      const imageWidth = 104;
      this.mediaLimit = Math.ceil(thumbnailWidth / imageWidth) || DEFAULT_MEDIA_LIMIT;
      if (!this.albumMedia || this.albumMedia.length === 0) {
        return;
      }
      const maxStartIndex = Math.max(0, this.albumMedia.length - this.mediaLimit);
      if (this.slide > maxStartIndex) {
        this.setThumbnailIndexes(maxStartIndex);
      } else if (this.slide >= 0) {
        this.setThumbnailIndexes(Math.max(0, Math.min(this.slide, this.thumbnailStartIndex)));
      } else {
        this.setThumbnailIndexes(0);
      }
    },
    initMedia() {
      let index = 0;
      const mediaId = this.mediaId || this.$route.params.mediaId;
      this.albumMedia = this.album.filter(
        media =>
          media.conversionStatus !== conversionStatusTypes.PROCESSING &&
          (media.file?.scanningStatus === ScanningStatusEnum.AVAILABLE ||
            media.file?.scanningStatus === ScanningStatusEnum.BYPASSED)
      );
      const albumMediaIndex = this.albumMedia.findIndex(m => m.id == mediaId);
      if (mediaId && albumMediaIndex > -1) {
        index = albumMediaIndex;
      }
      this.slide = index;
      this.prevSlide = this.slide;
    },
    async getMoreMedia(index, limit) {
      if (this.parent != parentTypes.GALLERY || !this.$parent.moreMediasExist) {
        return;
      }
      const params = {
        albumId: this.activeAlbum?.id,
        userSpecificAlbum: this.activeAlbum?.id == null,
        filterBy: this.filterBy,
        filterInstProfileIds: this.filterInstProfileIds,
        index,
        limit,
      };
      if (this.sortOn && this.orderDirection) {
        params.sortOn = this.sortOn;
        params.orderDirection = this.orderDirection;
      }
      await this.getAlbumMedia(params);
    },
    setThumbnailIndexes(thumbnailStartIndex) {
      const mediaLimit = this.mediaLimit || DEFAULT_MEDIA_LIMIT;
      this.thumbnailStartIndex = thumbnailStartIndex;
      this.thumbnailEndIndex =
        thumbnailStartIndex + Math.min(this.albumMedia.length - thumbnailStartIndex, mediaLimit) - 1;
    },
    onKeyDown(event) {
      if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
        event.preventDefault();
        this.handlePrint();
      }
    },
    handlePrint() {
      const printDocument = this.buildMediaPrintDocument({
        imageUrl: this.activeMedia.file.url,
        title: this.activeMedia.title || '',
        creator: this.activeMedia.creator?.name || '',
        date: this.mediaCreatedLongDate || '',
        description: this.activeMedia.description || '',
      });

      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'width:0;height:0;border:0;';
      document.body.appendChild(iframe);

      iframe.contentWindow.document.write(printDocument);
      iframe.contentWindow.document.close();

      iframe.onload = () => {
        iframe.contentWindow.print();
        iframe.remove();
      };
    },
    buildMediaPrintDocument({ imageUrl, title, creator, date, description }) {
      return `
        <!DOCTYPE html>
        <html>
          <head>
            <title>${title}</title>
            <style>${MEDIA_PRINT_WINDOW_STYLES}</style>
          </head>
          <body>
            <div class="content">
              <img src="${imageUrl}" />
              <h2>${title}</h2>
              <p>${creator} (${date})</p>
              <p>${description}</p>
            </div>
          </body>
        </html>
      `;
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@use '../../../shared/assets/scss/core/variables' as *;
@use '../../../shared/assets/scss/core/breakpoints' as *;
.media-shared-with {
  ::v-deep .dropdown-menu div {
    float: right;
  }
}
.print_tag {
  &:after {
    content: ', ';
  }
  &:last-child:after {
    content: '';
  }
}
.carousel.fromSearch ::v-deep {
  .carousel-control-prev,
  .carousel-control-next {
    display: none;
  }
}
.media-creator {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  width: 100%;
  @include breakpoint-lg() {
    margin-top: -20px;
  }
  .user {
    width: 50px;
  }
  .media-creator-infor {
    flex-grow: 1;
  }
  .media-creator-name {
    font-size: 16px;
    font-weight: bold;
  }
  .media-date {
    font-size: 14px;
    color: var(--color-grey);
  }
}

.media-info {
  overflow-wrap: break-word;
}

.failed-conversion-container {
  --questionmark-icon-background-color: var(--color-grey-base);
  border-top: 1px solid $color-grey-darker;
  border-bottom: 1px solid $color-grey-darker;
  padding-top: 10px;
  padding-bottom: 10px;
  margin-bottom: 10px;
}

.small-thumbnail {
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;

  &:not([aria-selected='true']) {
    opacity: 0.6;
  }

  > * {
    --width: 100px;
    --height: 100px;
  }
}

::v-deep .media-preview-modal {
  font-size: 14px;

  .modal-content {
    min-height: auto;
    overflow: hidden;

    .modal-body {
      padding: 0;
    }
  }

  @include breakpoint-lg() {
    .modal-dialog {
      max-width: 70vw;
      width: 70vw;
    }

    .modal-content {
      min-height: 500px;
      max-height: calc(100vh - 3.5rem);
    }

    .media-details {
      display: flex;
      flex-direction: column;
      min-height: 0;
      max-height: calc(100vh - 3.5rem);
    }

    .media-info {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      min-height: 0;
    }

    .media-description {
      flex: 0 1 auto;
      min-height: 0;
      overflow-y: auto;
    }

    .media-tags,
    .media-shared-with {
      flex-shrink: 0;
    }
  }

  @include breakpoint-lg-down() {
    .modal-dialog {
      height: 100%;

      .modal-header {
        --modal-accent-background: var(--color-primary-darker);
        border-radius: 0;
      }
    }
  }
}

.media-information {
  border-left: 1px solid var(--color-grey-base);
}
</style>
